/* Copyright (c) 2017 Backpack Health LLC */

import React from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';
import SwipeableViews from 'react-swipeable-views';
import { connect } from 'react-redux';
import classnames from 'classnames';

import './Intro.styles.scss';

import ProgressDots from '../../components/ProgressDots';

import ImagePortableStatic from '../../../design-assets/images/bp-intro-setup-01-portable.png';
import ImageManagableStatic from '../../../design-assets/images/bp-intro-setup-02-managable.png';
import ImageSharableStatic from '../../../design-assets/images/bp-intro-setup-03-sharable.png';
import ImageTranslatableStatic from '../../../design-assets/images/bp-intro-setup-04-translatable.png';

import ImagePortable from '../../../design-assets/images/bp-intro-setup-01-portable.gif';
import ImageManagable from '../../../design-assets/images/bp-intro-setup-02-managable.gif';
import ImageSharable from '../../../design-assets/images/bp-intro-setup-03-sharable.gif';
import ImageTranslatable from '../../../design-assets/images/bp-intro-setup-04-translatable.gif';

import { selectors as listSelectors } from '../../redux/ducks/lists/lists';
import ColorThemeModel from '../../../models/ColorThemeModel';
import RouteNames from '../../appAuthorized/RouteNames';
import PrivacyIntroContent from '../views/PrivacyIntro/PrivacyIntroContent';
import { AppNavCancelButton } from '../../components/appNav/appNavBits/AppNavButtons';
import { navigateBack } from '../../appAuthorized/redux/NavigationActions';

function mapStateToProps(state, routerProps) {
  const activeColorTheme = new ColorThemeModel(listSelectors.getCurrentColorTheme(state));

  return {
    baseColor: activeColorTheme.highlightColorCss(),
    darkAccentColor: activeColorTheme.accentColorCss(),
    startAtEnd: routerProps.location.pathname === RouteNames.introEnd(),
    goToLogin: () => {
      navigateBack(RouteNames.login());
    },
    goToSignup: () => {
      navigateBack(RouteNames.signup());
    },
    goToAndroidIntro: () => {
      navigateBack(RouteNames.intro());
    },
  };
}

const Page1 = (props) => (
  <div className="cmp-page-intro-content-wrapper">
  <div className="cmp-page-intro-content">
    <div className="image">
      <img src={props.isActive ? ImagePortable : ImagePortableStatic} width="100%" alt="" />
    </div>

    <div className="description">
      <h3>
        <FormattedMessage id="signupIntro.p1.heading.text" />
      </h3>
      <p>
        <FormattedMessage id="signupIntro.p1.message.text" />
      </p>
    </div>
  </div>
  </div>
);
Page1.propTypes = {
  isActive: PropTypes.bool,
};

const Page2 = (props) => (
  <div className="cmp-page-intro-content-wrapper">
  <div className="cmp-page-intro-content">
    <div className="image">
      <img src={props.isActive ? ImageManagable : ImageManagableStatic} width="100%" alt="" />
    </div>

    <div>
    <h3>
      <FormattedMessage id="signupIntro.p2.heading.text" />
    </h3>
    <p>
      <FormattedMessage id="signupIntro.p2.message.text" />
    </p>
  </div>
  </div>
  </div>
);
Page2.propTypes = {
  isActive: PropTypes.bool,
};

const Page3 = (props) => (
  <div className="cmp-page-intro-content-wrapper">
  <div className="cmp-page-intro-content">
    <div className="image">
      <img src={props.isActive ? ImageSharable : ImageSharableStatic} width="100%" alt="" />
    </div>

    <div>
    <h3>
      <FormattedMessage id="signupIntro.p3.heading.text" />
    </h3>
    <p>
      <FormattedMessage id="signupIntro.p3.message.text" />
    </p>
  </div>
  </div>
  </div>
);
Page3.propTypes = {
  isActive: PropTypes.bool,
};

const Page4 = (props) => (
  <div className="cmp-page-intro-content-wrapper">
  <div className="cmp-page-intro-content">
    <div className="image">
      <img src={props.isActive ? ImageTranslatable : ImageTranslatableStatic} width="100%" alt="" />
    </div>

    <div>
    <h3>
      <FormattedMessage id="signupIntro.p4.heading.text" />
    </h3>
    <p>
      <FormattedMessage id="signupIntro.p4.message.text" />
    </p>
  </div>
  </div>
  </div>
);
Page4.propTypes = {
  isActive: PropTypes.bool,
};

class Page5 extends React.Component {

  componentDidMount() {
    const el = document.getElementsByClassName('cmp-privacyintrocontent')[0].parentElement;
    el.addEventListener('resize', this.onScroll);
    el.addEventListener('scroll', this.onScroll);
  }

  componentWillUnmount() {
    const el = document.getElementsByClassName('cmp-privacyintrocontent')[0].parentElement;
    el.removeEventListener('resize', this.onScroll);
    el.removeEventListener('scroll', this.onScroll);
  }

  onScroll = (e) => {
    this.props.onScroll(e.target.scrollTop);
  }

  render() {
    return (
      <PrivacyIntroContent
        onAccept={this.props.goToSignup}
        style={{
          paddingBottom: 48,
          marginTop: 60,
        }}
        isNewUser={true}
      />
    );
  }
}
Page5.propTypes = {
  goToSignup: PropTypes.func,
  isActive: PropTypes.bool,
  onScroll: PropTypes.func,
};

class Intro extends React.Component {

  constructor(props) {
    super(props);

    this.viewRef = React.createRef();
    this.state = {
      step: props.startAtEnd ? 5 : 1,
      isScrolled: false,
      height: window.innerHeight,
    };
  }

  onCancel = () => {
    this.setState({ step: 1 });
  }

  toggleIsScrolled = (scrollPos) => {
    if (scrollPos > 10) {
      if (!this.state.isScrolled) {
        this.setState({
          isScrolled: true,
          height: window.innerHeight,
        });
      }
    } else if (this.state.isScrolled) {
      this.setState({
        isScrolled: false,
        height: window.innerHeight,
      });
    }
  }

  handleOnChangeStep = (i) => {
    this.setState({ step: i + 1 });
  }

  forceStep = () => {
    const isLastStep = this.state.step === 5;
    if (isLastStep) {
      return 4;
    } else if (this.state.step === 1) {
      return 0;
    }

    return null;
  }

  render() {
    const isLastStep = this.state.step === 5;
    const index = this.forceStep();

    return (
      <div
        className={classnames('cmp-page-intro', { scrolled: isLastStep && this.state.isScrolled })}
        style={{
          background: `linear-gradient(150.49deg, ${this.props.baseColor} 0.51%, ${this.props.darkAccentColor} 100%)`,
        }}
      >
        <div className="header">
          {
            isLastStep ? (
              <AppNavCancelButton color="white" onClick={this.onCancel} />
            ) : <div />
          }
          {
            isLastStep ? <div /> : (
              <a onClick={this.props.goToLogin} style={{ fontWeight: 'bold' }}>
                <FormattedMessage id="signupIntro.login.button.label" />
              </a>
            )
          }
        </div>

        <div className="cmp-pageintro-swipablecontent">
          <div className="cmp-pageintro-swipablecontent-container">
            <SwipeableViews
              index={index}
              onChangeIndex={this.handleOnChangeStep}
              containerStyle={{
                height: this.state.height,
              }}
            >
              <Page1 isActive={this.state.step === 1} />
              <Page2 isActive={this.state.step === 2} />
              <Page3 isActive={this.state.step === 3} />
              <Page4 isActive={this.state.step === 4} />
              <Page5 isActive={this.state.step === 5} goToSignup={this.props.goToSignup} onScroll={this.toggleIsScrolled} />
            </SwipeableViews>

            <div className="footer" style={{ visibility: this.state.step < 5 ? 'visible' : 'hidden' }}>
              <div className="steps">
                <ProgressDots stepTotal={5} currentStep={this.state.step} />
              </div>

              <a onClick={() => { this.setState({ step: 5 }); }}>
                <FormattedMessage id="signupIntro.skip.button.label" />
              </a>
            </div>
          </div>
        </div>
      </div>
    );
  }
}
Intro.propTypes = {
  goToLogin: PropTypes.func,
  goToSignup: PropTypes.func,
  startAtEnd: PropTypes.bool,
  baseColor: PropTypes.string,
  darkAccentColor: PropTypes.string,
};

export default connect(mapStateToProps)(Intro);
